import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PremiumProvider } from './context/PremiumContext';
import { Toaster } from './components/ui/sonner';
import { GlobalBackButton } from './components/GlobalBackButton';
import { AICoachBubble } from './components/AICoachBubble';
import { LandingPage } from './pages/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import BadgeCelebrationPreview from './pages/BadgeCelebrationPreview';
import BadgesPage from './pages/BadgesPage';
import LiveWorkoutRoomPage from './pages/LiveWorkoutRoomPage';
import LiveWorkoutPreflightPage from './pages/LiveWorkoutPreflightPage';
import SessionRecordingsPage from './pages/SessionRecordingsPage';
import ActivityPage from './pages/ActivityPage';
import { WorkoutsPage } from './pages/WorkoutsPage';
import { WorkoutDetailPage } from './pages/WorkoutDetailPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { HistoryPage } from './pages/HistoryPage';
import { NutritionPage } from './pages/NutritionPage';
import { NutritionPlansPurchasePage } from './pages/NutritionPlansPurchasePage';
import { NutritionPurchaseSuccessPage } from './pages/NutritionPurchaseSuccessPage';
import { MyNutritionPlansPage } from './pages/MyNutritionPlansPage';
import { NutritionPlanViewerPage } from './pages/NutritionPlanViewerPage';
import WalkthroughPage from './pages/WalkthroughPage';
import { ProfilePage } from './pages/ProfilePage';
import { ProgressPage } from './pages/ProgressPage';
import { HRSessionsPage } from './pages/HRSessionsPage';
import { WorkoutBuilderPage } from './pages/WorkoutBuilderPage';
import { SchedulePage } from './pages/SchedulePage';
import { ProgramsPage } from './pages/ProgramsPage';
import { ProgramDetailPage } from './pages/ProgramDetailPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { ExerciseLibraryPage } from './pages/ExerciseLibraryPage';
import { FoodLogPage } from './pages/FoodLogPage';
import { WaterPage } from './pages/WaterPage';
import { AdminProgramEditorPage } from './pages/AdminProgramEditorPage';
import { AdminWorkoutEditorPage } from './pages/AdminWorkoutEditorPage';
import { TimerPage } from './pages/TimerPage';
import { SquatDemo3DPage } from './pages/SquatDemo3DPage';
import { MessagesPage } from './pages/MessagesPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { MealPlanPage } from './pages/MealPlanPage';
import { WearablesPage } from './pages/WearablesPage';
import { SocialFeedPage } from './pages/SocialFeedPage';
import { FollowListPage } from './pages/FollowListPage';
import { ChallengesPage } from './pages/ChallengesPage';
import { ChallengeDetailPage } from './pages/ChallengeDetailPage';
import { TeamDetailPage } from './pages/TeamDetailPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { DirectMessagesPage, ConversationPage } from './pages/DirectMessagesPage';
import { DiscoverPage } from './pages/DiscoverPage';
import { BuddiesPage } from './pages/BuddiesPage';
import { GroupsPage } from './pages/GroupsPage';
import { GroupDetailPage } from './pages/GroupDetailPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { LiveVideoProvider } from './context/LiveVideoContext';
import { PresenceProvider } from './context/PresenceContext';
import { IncomingCallProvider } from './context/IncomingCallContext';
import { WorkoutStatusProvider } from './context/WorkoutStatusContext';
import FloatingLiveVideo from './components/live/FloatingLiveVideo';
import IncomingCallModal from './components/live/IncomingCallModal';
import VersionChecker from './components/VersionChecker';
import { FloatTimerButton } from './components/timer/FloatTimerButton';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/walkthrough" element={<ProtectedRoute><WalkthroughPage /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
      <Route path="/live-workout/:sessionId" element={<ProtectedRoute><LiveWorkoutPreflightPage /></ProtectedRoute>} />
      <Route path="/live-workout/:sessionId/room" element={<ProtectedRoute><LiveWorkoutRoomPage /></ProtectedRoute>} />
      <Route path="/live-workout/:sessionId/recordings" element={<ProtectedRoute><SessionRecordingsPage /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
      <Route path="/preview/badge-celebration" element={<ProtectedRoute><BadgeCelebrationPreview /></ProtectedRoute>} />
      <Route path="/workouts" element={<ProtectedRoute><WorkoutsPage /></ProtectedRoute>} />
      <Route path="/workout/:id" element={<ProtectedRoute><WorkoutDetailPage /></ProtectedRoute>} />
      {/* Plural alias — several call-sites historically link to /workouts/:id */}
      <Route path="/workouts/:id" element={<ProtectedRoute><WorkoutDetailPage /></ProtectedRoute>} />
      <Route path="/workout-builder" element={<ProtectedRoute><WorkoutBuilderPage /></ProtectedRoute>} />
      <Route path="/exercises" element={<ProtectedRoute><ExerciseLibraryPage /></ProtectedRoute>} />
      <Route path="/demo/squat-3d" element={<ProtectedRoute><SquatDemo3DPage /></ProtectedRoute>} />
      <Route path="/food-log" element={<ProtectedRoute><FoodLogPage /></ProtectedRoute>} />
      <Route path="/water" element={<ProtectedRoute><WaterPage /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/nutrition" element={<ProtectedRoute><NutritionPage /></ProtectedRoute>} />
      <Route path="/nutrition-plans" element={<ProtectedRoute><NutritionPlansPurchasePage /></ProtectedRoute>} />
      {/* Stripe success pages are deliberately PUBLIC routes — when a
          mobile user returns from `checkout.stripe.com`, the auth cookie
          can be stripped by browser tracking protection on the cross-site
          redirect. Wrapping these in ProtectedRoute would bounce paying
          customers to /login (terrible UX). The actual purchase unlock is
          handled by the Stripe webhook server-side; the success page is
          purely cosmetic. */}
      <Route path="/nutrition-plans/success" element={<NutritionPurchaseSuccessPage />} />
      <Route path="/nutrition-plans/my" element={<ProtectedRoute><MyNutritionPlansPage /></ProtectedRoute>} />
      <Route path="/nutrition-plans/view/:planId" element={<ProtectedRoute><NutritionPlanViewerPage /></ProtectedRoute>} />
      <Route path="/timer" element={<ProtectedRoute><TimerPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      {/* /dm (no user id) → send back to the messages hub. Fixes the long-standing 404. */}
      <Route path="/dm" element={<Navigate to="/messages" replace />} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/meal-plans" element={<ProtectedRoute><MealPlanPage /></ProtectedRoute>} />
      <Route path="/wearables" element={<ProtectedRoute><WearablesPage /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><SocialFeedPage /></ProtectedRoute>} />
      <Route path="/social/followers" element={<ProtectedRoute><FollowListPage mode="followers" /></ProtectedRoute>} />
      <Route path="/social/following" element={<ProtectedRoute><FollowListPage mode="following" /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
      <Route path="/buddies" element={<ProtectedRoute><BuddiesPage /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/groups/:groupId" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
      <Route path="/challenges/:id" element={<ProtectedRoute><ChallengeDetailPage /></ProtectedRoute>} />
      <Route path="/challenges/:challengeId/team/:teamId" element={<ProtectedRoute><TeamDetailPage /></ProtectedRoute>} />
      <Route path="/user/:userId" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      <Route path="/dm" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
      <Route path="/dm/:userId" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/hr-sessions" element={<ProtectedRoute><HRSessionsPage /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/programs" element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>} />
      <Route path="/program/:id" element={<ProtectedRoute><ProgramDetailPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/:section" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/program/:id" element={<ProtectedRoute><AdminProgramEditorPage /></ProtectedRoute>} />
      <Route path="/admin/workout/:id" element={<ProtectedRoute><AdminWorkoutEditorPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PremiumProvider>
          <WorkoutStatusProvider>
            <LiveVideoProvider>
              <PresenceProvider>
                <IncomingCallProvider>
                  <AppRoutes />
                  <GlobalBackButton />
                  <AICoachBubble />
                  <FloatingLiveVideo />
                  <IncomingCallModal />
                  <VersionChecker />
                  <FloatTimerButton />
                  <Toaster position="top-right" richColors />
                </IncomingCallProvider>
              </PresenceProvider>
            </LiveVideoProvider>
          </WorkoutStatusProvider>
        </PremiumProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
